/**
 * Performs a deep merge of objects and returns new object. Does not modify
 * objects (immutable) and merges arrays via concatenation.
 *
 * @param {...Object} objects - Objects to merge
 * @returns {Object} New object with merged key/values
 */
export function mergeDeep( ...objects: Object[] ): Object {
  const isObject = obj => obj && typeof obj === 'object';

  return objects.reduce(( prev, obj ) => {
    Object.keys( obj ).forEach( key => {
      const pVal = prev[key];
      const oVal = obj[key];

      if ( Array.isArray( pVal ) && Array.isArray( oVal )) {
        prev[key] = pVal.concat( ...oVal );
      }
      else if ( isObject( pVal ) && isObject( oVal )) {
        prev[key] = mergeDeep( pVal, oVal );
      }
      else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {});
}

/**
 * Removes undefined, null, or NaN values and their corroponding properties
 * from the passed in Object (immutable)
 *
 * @param {Object} obj to shrink
 * @returns {Object} New Object with properties removed
 */
export function shrink( obj: Object ): Object {
   if ( !obj || !Object.keys( obj ).length ) { return; }
   if ( Array.isArray( obj ) && !obj.length ) { return; }

   Object.entries( obj ).map(([ key, value ]) => {
      if ([ undefined, null, NaN ].includes( value )) { delete obj[key]; }
      else if ( typeof value === 'object' ) {
         shrink( obj[key] );

         if ( Array.isArray( obj[key] )) { obj[key] = obj[key].filter( Boolean ); }
         if ( !Object.keys( obj[key] ).length ) { delete obj[key]; }
      }
   });

   return obj;
}

/**
 * Removes properties in first object that exist and are equal in any of the succeeding objects
 * also shrinks the object to remove undefined, null or NaN values
 *
 * @param {object} ...objects objects that you want to compare
 * @returns {object} object that has properties that exist in succeeding objects removed
 */
export function intercect( ...objects: Object[] ): {[ key: string ]: any } {
   let ret: Object = undefined;

   for ( const obj of objects ) {
      if ( !ret ) { ret = obj; continue; }

      for ( const [ key, value ] of Object.entries( obj )) {
         if ( ret.hasOwnProperty( key )) {
            if ( value === ret[key] ) { delete ret[key]; }
            else if ( typeof value === 'object' && !!value ) { ret[key] = intercect( ret[key], value ); }
         }
      }
   }

   return shrink( ret );
}
